import { Author, Readable } from 'generated/apolloComponents'

import React, { useRef } from 'react'
import useTranslation from 'next-translate/useTranslation'
import Avatar from '@/components/atoms/Avatar'
import ArticlePreview from '@/components/organisms/ArticlePreview/ArticlePreview'
import ButtonExternal from '@/components/atoms/ButtonExternal'
import GithubIcon from '../atoms/SocialIcons/GithubIcon'
import TwitterIcon from '../atoms/SocialIcons/TwitterIcon'
import LinkedinIcon from '../atoms/SocialIcons/LinkedinIcon'

interface IProps {
  author: Author
  articles: Readable[]
  refs?: any
}

const AuthorTemplate = ({ author, articles, refs }: IProps) => {
  const { t, lang } = useTranslation('author')
  const sectionRefs = [useRef(null), useRef(null), useRef(null), useRef(null)]

  return (
    <main className='bg-gray-bg py-4 overflow-hidden'>
      <div className='container h-full'>
        <div className='flex flex-col justify-between h-full'>
          <div className='bg-white pt-6 rounded-xl px-6 md:pl-10 md:pr-6 h-full'>
            <div className='text-center md:flex md:text-left mt-4 md:mt-0'>
              {author.photo ? (
                <Avatar
                  url={author.photo?.url}
                  size='large'
                  remote
                  className='md:pr-10 py-6 flex flex-col items-center'
                />
              ) : null}
              <div className='flex flex-col justify-center'>
                <h1 className={`text-c30 md:text-6xl font-medium `}>
                  {author.name}
                </h1>
                {author.company ? (
                  <h4>
                    {author.position} | {author.company}
                  </h4>
                ) : (
                  <h4>{author.position}</h4>
                )}
                <div className='md:block my-4 text-action whitespace-nowrap'>
                  {author.linkedin && (
                    <a
                      href={author.linkedin}
                      title='Linkedin'
                      target='_blank'
                      rel='nofollow noopener noreferrer'
                      className='align-middle'
                    >
                      <LinkedinIcon width={21} className='mr-4' />
                    </a>
                  )}
                  {author.github && (
                    <a
                      href={author.github}
                      title='Github'
                      target='_blank'
                      rel='nofollow noopener noreferrer'
                      className='align-middle'
                    >
                      <GithubIcon width={21} className='mr-4' />
                    </a>
                  )}
                  {author.twitter && (
                    <a
                      href={author.twitter}
                      title='Twitter'
                      target='_blank'
                      rel='nofollow noopener noreferrer'
                      className='align-middle'
                    >
                      <TwitterIcon width={21} className='mr-4' />
                    </a>
                  )}
                  {author.www && (
                    <a
                      href={author.www}
                      title={author.www}
                      target='_blank'
                      rel='nofollow noopener noreferrer'
                    >
                      {author.www}
                    </a>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
        {author.bio ? (
          <div
            id='about'
            className='bg-white rounded-xl px-6 md:px-10 py-6 md:py-10 my-4'
            ref={sectionRefs[0]}
          >
            <h2 className='text-2xl md:text-4xl font-medium mb-5'>Bio</h2>
            <div
              className='content md:w-10/12 font-normal leading-normal'
              dangerouslySetInnerHTML={{ __html: author.bio }}
            />
          </div>
        ) : null}
        {articles.length !== 0 ? (
          <div
            id='readme'
            className=' rounded-xl  md:px-10  md:py-10 mt-8 md:mt-0 md:my-4'
            ref={sectionRefs[2]}
          >
            <h2 className='text-2xl md:text-4xl font-medium'>
              {author.name} / {t('author:articles')}
            </h2>
            <div className='lg:flex lg:-mx-6 flex-wrap mt-4 md:mt-10'>
              {articles.map((item, index) => (
                <ArticlePreview key={index} size='small' article={item} />
              ))}
            </div>
          </div>
        ) : null}
        <section className='flex flex-wrap justify-center my-14 md:my-24 items-center'>
          <h3 className='w-full md:w-auto text-center text-2xl md:text-4xl'>
            {t('author:share')}
          </h3>
          <ButtonExternal
            href='mailto:content@bulldogjob.pl'
            className='ml-0 lg:ml-20 md:py-4 text-xl md:text-3xl mt-6 lg:mt-0'
            type='action'
          >
            {t('author:become_an_author')}
          </ButtonExternal>
        </section>
      </div>
    </main>
  )
}

export default AuthorTemplate
